import React, { useEffect, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { MapPin, Navigation } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Default center (Mumbai, India)
const DEFAULT_CENTER = { lat: 19.076, lng: 72.8777 };

// Custom marker component
const CustomMarker = ({ type, label }) => (
  <div className={`
    w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg
    ${type === 'pickup' ? 'bg-green-500' : type === 'drop' ? 'bg-red-500' : 'bg-orange-500'}
  `}>
    {label || (type === 'pickup' ? 'P' : 'D')}
  </div>
);

// Route polyline component
const RoutePolyline = ({ origin, destination }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map || !window.google || !origin || !destination) return;
    
    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#F97316',
        strokeWeight: 4,
        strokeOpacity: 0.8
      }
    });
    
    directionsService.route({
      origin: origin,
      destination: destination,
      travelMode: window.google.maps.TravelMode.DRIVING
    }, (result, status) => {
      if (status === 'OK') {
        directionsRenderer.setDirections(result);
      }
    });
    
    return () => {
      directionsRenderer.setMap(null);
    };
  }, [map, origin, destination]);
  
  return null;
};

// Map content with markers and route
const MapContent = ({ pickupLocation, dropLocation, showRoute }) => {
  return (
    <>
      {pickupLocation && (
        <AdvancedMarker position={pickupLocation}>
          <CustomMarker type="pickup" />
        </AdvancedMarker>
      )}
      {dropLocation && (
        <AdvancedMarker position={dropLocation}>
          <CustomMarker type="drop" />
        </AdvancedMarker>
      )}
      {showRoute && pickupLocation && dropLocation && (
        <RoutePolyline origin={pickupLocation} destination={dropLocation} />
      )}
    </>
  );
};

// Main DeliveryMap component
export const DeliveryMap = ({ 
  pickupLocation, 
  dropLocation, 
  showRoute = true,
  height = "300px",
  className = ""
}) => {
  const [center, setCenter] = useState(DEFAULT_CENTER);
  
  useEffect(() => {
    if (pickupLocation) {
      setCenter(pickupLocation);
    } else if (dropLocation) {
      setCenter(dropLocation);
    }
  }, [pickupLocation, dropLocation]);
  
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div 
        className={`bg-secondary/30 flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Map unavailable</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={className} style={{ height }}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={13}
          mapId="setuxpress-map"
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={true}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
        >
          <MapContent 
            pickupLocation={pickupLocation} 
            dropLocation={dropLocation}
            showRoute={showRoute}
          />
        </Map>
      </APIProvider>
    </div>
  );
};

// Tracking map with live driver position
export const TrackingMap = ({ 
  pickupLocation, 
  dropLocation, 
  driverLocation,
  status = "in_transit",
  height = "400px",
  className = ""
}) => {
  const [center, setCenter] = useState(DEFAULT_CENTER);
  
  useEffect(() => {
    if (driverLocation) {
      setCenter(driverLocation);
    } else if (pickupLocation) {
      setCenter(pickupLocation);
    }
  }, [driverLocation, pickupLocation]);
  
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div 
        className={`bg-secondary/30 flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <Navigation className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Live tracking unavailable</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={className} style={{ height }}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={14}
          mapId="setuxpress-tracking"
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={true}
        >
          {pickupLocation && (
            <AdvancedMarker position={pickupLocation}>
              <CustomMarker type="pickup" />
            </AdvancedMarker>
          )}
          {dropLocation && (
            <AdvancedMarker position={dropLocation}>
              <CustomMarker type="drop" />
            </AdvancedMarker>
          )}
          {driverLocation && (
            <AdvancedMarker position={driverLocation}>
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Navigation className="w-5 h-5 text-white" />
              </div>
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>
    </div>
  );
};

// Geocoding helper - convert address to coordinates
export const geocodeAddress = async (address) => {
  // Check if Google Maps API is fully loaded
  if (!GOOGLE_MAPS_API_KEY) return null;
  if (!window.google?.maps?.Geocoder) {
    // API not loaded yet - return null silently
    return null;
  }
  
  try {
    const geocoder = new window.google.maps.Geocoder();
    
    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          resolve(null);
        }
      });
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Geocoding error:', error);
    }
    return null;
  }
};

// Distance calculation helper
export const calculateDistance = async (origin, destination) => {
  if (!GOOGLE_MAPS_API_KEY || !window.google) return null;
  
  const service = new window.google.maps.DistanceMatrixService();
  
  return new Promise((resolve) => {
    service.getDistanceMatrix({
      origins: [origin],
      destinations: [destination],
      travelMode: window.google.maps.TravelMode.DRIVING
    }, (response, status) => {
      if (status === 'OK' && response.rows[0]?.elements[0]?.status === 'OK') {
        const element = response.rows[0].elements[0];
        resolve({
          distance: element.distance.value / 1000, // km
          distanceText: element.distance.text,
          duration: element.duration.value / 60, // minutes
          durationText: element.duration.text
        });
      } else {
        resolve(null);
      }
    });
  });
};

export default DeliveryMap;
